import { IAnyType, IServerProps } from 'common/types/common';
import { getValidatedDynamicParam } from 'common/utils/dynamicRoute';
import { buildAbsoluteUrl } from 'common/utils/url';
import { promises as fs } from 'fs';
import { Layout } from 'layout';
import { EmployeeProfile } from 'modules/about/EmployeeProfile';
import { GetServerSidePropsContext } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import path from 'path';

const EmployeePage = ({ data }: IEmployeePageProps) => {
  const { t } = useTranslation('pageEmployees');
  const employee = data.employee;

  if (!employee) {
    return null;
  }

  const schemaMarkup = [
    {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: employee.name,
      jobTitle: employee.role,
      description: employee.seo.description,
      url: buildAbsoluteUrl(t('common:company.url'), employee.seo.slug),
      worksFor: {
        '@type': 'Organization',
        name: 'Nordit d.o.o.',
      },
      knowsAbout: employee.schema.knowsAbout,
    },
  ];

  const seo = {
    alternate: employee.alternate,
    description: employee.seo.description,
    image: employee.seo.image,
    languageSwitch: {
      localizations: employee.localizations,
    },
    schemaMarkup,
    slug: employee.seo.slug,
    title: employee.seo.title,
  };

  return (
    <Layout className="page-light bg-gradient blog-content" footerBorderTop={false} seo={seo}>
      <EmployeeProfile employee={employee} />
    </Layout>
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
): Promise<IServerProps<IEmployeePageProps>> => {
  const localeKey = context.locale || 'en';
  const employeeSlug = getValidatedDynamicParam(context.query.employeeSlug);

  if (!employeeSlug) {
    return {
      notFound: true,
    };
  }

  const filePath = path.join(process.cwd(), 'public', 'locales', localeKey, 'pageEmployees.json');
  const fileContent = await fs.readFile(filePath, 'utf8');
  const employees = JSON.parse(fileContent).employees || [];
  const employee = employees.find((item: IAnyType) => item.slug === employeeSlug) || null;

  if (!employee) {
    return {
      notFound: true,
    };
  }

  const locale = await serverSideTranslations(localeKey, ['common', 'meta', 'pageEmployees']);

  return {
    props: {
      data: {
        employee,
        employeeSlug,
        referer: context.req.headers.referer || null,
      },
      ...locale,
    },
  };
};

interface IEmployeePageProps {
  data:
    | {
        employee: IAnyType;
        employeeSlug: string;
        referer?: string | null;
      }
    | Record<string, never>;
}

export default EmployeePage;
